/* eslint-disable react/no-array-index-key */
import React, {
  useEffect, useRef, useState, ClipboardEvent, KeyboardEvent, ChangeEvent,
} from 'react';
import { Box, Stack, TextField } from '@mui/material';

export const OTP_FIELD_LENGTH = 4;

export interface OTPFieldProps {
  length?: number;
  value?: string;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?: (code: string) => void;
  onComplete?: (code: string) => void;
}

const isDigit = (char: string) => /^[0-9]$/.test(char);

const OTPField = ({
  length = OTP_FIELD_LENGTH,
  value,
  error = false,
  disabled = false,
  autoFocus = true,
  onChange,
  onComplete,
}: OTPFieldProps): React.ReactElement => {
  const isControlled = value !== undefined;
  const [internalDigits, setInternalDigits] = useState<string[]>(
    () => Array(length).fill(''),
  );
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const completedCodeRef = useRef<string | null>(null);

  const digits = isControlled
    ? Array.from({ length }, (_, i) => value?.[i] ?? '')
    : internalDigits;

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const emitChange = (nextDigits: string[]) => {
    const code = nextDigits.join('');
    if (!isControlled) {
      setInternalDigits(nextDigits);
    }
    onChange?.(code);

    if (code.length === length && nextDigits.every((d) => d !== '')) {
      if (completedCodeRef.current !== code) {
        completedCodeRef.current = code;
        onComplete?.(code);
      }
    } else {
      completedCodeRef.current = null;
    }
  };

  const updateDigit = (index: number, digit: string) => {
    const next = [...digits];
    next[index] = digit;
    emitChange(next);
  };

  const focusInput = (index: number) => {
    const clamped = Math.max(0, Math.min(length - 1, index));
    inputRefs.current[clamped]?.focus();
    inputRefs.current[clamped]?.select();
  };

  const handleChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === '') {
      updateDigit(index, '');
      return;
    }

    const digitChars = raw.replace(/\D/g, '');
    if (!digitChars) {
      return;
    }

    if (digitChars.length > 1) {
      const next = [...digits];
      digitChars.split('').slice(0, length - index).forEach((char, offset) => {
        next[index + offset] = char;
      });
      emitChange(next);
      focusInput(Math.min(index + digitChars.length, length - 1));
      return;
    }

    if (!isDigit(digitChars)) {
      return;
    }

    updateDigit(index, digitChars);
    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        updateDigit(index, '');
      } else if (index > 0) {
        updateDigit(index - 1, '');
        focusInput(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) {
      return;
    }

    const next = [...digits];
    pasted.split('').slice(0, length - index).forEach((char, offset) => {
      next[index + offset] = char;
    });
    emitChange(next);
    focusInput(Math.min(index + pasted.length, length - 1));
  };

  return (
    <Stack direction="row" spacing={1.5} justifyContent="center">
      {digits.map((digit, index) => (
        <Box key={`otp-digit-${index}`}>
          <TextField
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            id={`otp-input-${index}`}
            variant="outlined"
            value={digit}
            disabled={disabled}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            onFocus={(e) => e.target.select()}
            error={error}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              autoComplete: index === 0 ? 'one-time-code' : 'off',
              maxLength: 1,
              'aria-label': `Digit ${index + 1} of ${length}`,
              style: {
                textAlign: 'center',
                fontSize: '1.5rem',
                width: '28px',
                height: '28px',
                padding: '10px',
              },
            }}
            sx={{
              width: '48px',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderRadius: '0px',
                  borderColor: error ? 'error.main' : '#070707',
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? 'error.main' : 'primary.main',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#E6E3DF',
                  color: '#B1AEAB',
                  WebkitTextFillColor: '#B1AEAB',
                  '& fieldset': {
                    borderColor: '#E6E3DF',
                  },
                },
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#B1AEAB',
                color: '#B1AEAB',
              },
            }}
          />
        </Box>
      ))}
    </Stack>
  );
};

export default OTPField;
